import { useEffect, useEffectEvent } from 'react'
import { useTranslation } from 'react-i18next'
import VisibilityDetector from 'components/VisibilityDetector/VisibilityDetector'
import { useBagAnalytics } from 'features/cart/hooks/useBagAnalytics'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import { preloadImagePromise } from 'utils/ImageUtils'
import { CART_UPSELLS } from '../cartUpsells.registry'
import type { CartUpsellCardData } from '../cartUpsells.types'
import { CartUpsellCardThumbnail } from './thumbnails/CartUpsellCardThumbnail'
import * as S from './CartUpsellCard.styles'

type CartUpsellCardProps = {
  card: CartUpsellCardData
  position: number
  onClick: () => void
  onDismiss: () => void
}

export function CartUpsellCard({
  card,
  position,
  onClick,
  onDismiss,
}: CartUpsellCardProps) {
  const { t } = useTranslation()
  const { trackBagEvent } = useBagAnalytics()
  const { cardCtaKey } = CART_UPSELLS[card.id]
  const preloadFlowImages = useEffectEvent(() => {
    card.flowImageUrls.forEach((imageUrl) => preloadImagePromise(imageUrl))
  })
  // Loaded with the card, so its Flow opens fully drawn.
  useEffect(() => {
    preloadFlowImages()
  }, [])
  const onCardVisible = () =>
    trackBagEvent(EVENT_NAMES.CART_UPSELL_CARD_SHOWN, {
      ...card.analyticsProps,
      'Card Position': position,
    })
  return (
    <VisibilityDetector onVisible={onCardVisible}>
      <S.Card>
        <S.OpenButton
          type="button"
          onClick={onClick}
          data-testid="cart-upsell-card"
          data-upsell-id={card.id}
        >
          <CartUpsellCardThumbnail thumbnail={card.thumbnail} />
          <S.Copy>
            <S.Title>{t(card.titleKey)}</S.Title>
            <S.Cta>{t(cardCtaKey)}</S.Cta>
          </S.Copy>
        </S.OpenButton>
        <S.DismissButton
          type="button"
          onClick={onDismiss}
          aria-label={t('cart_upsells.card_dismiss')}
          data-testid="cart-upsell-card-dismiss"
        >
          <S.DismissIcon />
        </S.DismissButton>
      </S.Card>
    </VisibilityDetector>
  )
}
